import api from './api';

const observabilityService = {
  /**
   * Get overview stats (cross-firm or per-firm)
   * @param {string|null} firmId - Optional firm UUID to filter by
   * @returns {Promise<{total_firms: number, total_users: number, total_cases: number, total_documents: number, firm_id: string|null}>}
   */
  getOverview: async (firmId = null) => {
    const params = firmId ? { firm_id: firmId } : {};
    const response = await api.get('/admin/observability/overview', { params });
    return response.data;
  },

  /**
   * Get paginated login history
   */
  getLoginHistory: async (firmId = null, page = 1, pageSize = 25) => {
    const params = { page, page_size: pageSize };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/users/login-history', { params });
    return response.data;
  },

  /**
   * Get DAU/WAU/MAU metrics with 30-day trend
   */
  getDauWauMau: async (firmId = null) => {
    const params = firmId ? { firm_id: firmId } : {};
    const response = await api.get('/admin/observability/users/dau-wau-mau', { params });
    return response.data;
  },

  /**
   * Get per-user last activity timestamps (paginated)
   */
  getUserLastActivity: async (firmId = null, page = 1, pageSize = 50) => {
    const params = { page, page_size: pageSize };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/users/last-activity', { params });
    return response.data;
  },

  /**
   * Get active sessions with browser/OS/device info
   */
  getSessions: async (firmId = null) => {
    const params = firmId ? { firm_id: firmId } : {};
    const response = await api.get('/admin/observability/users/sessions', { params });
    return response.data;
  },

  /**
   * Force logout a user by invalidating their session
   */
  forceLogout: async (userId) => {
    const response = await api.post('/admin/observability/users/sessions/force-logout', { user_id: userId });
    return response.data;
  },

  /**
   * Get feature usage counts with low-adoption flagging
   */
  getFeatureUsage: async (firmId = null, days = 30) => {
    const params = { days };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/features/usage', { params });
    return response.data;
  },

  /**
   * Get feature usage trends over time
   */
  getFeatureTrends: async (firmId = null, days = 30) => {
    const params = { days };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/features/trends', { params });
    return response.data;
  },

  // --- Error Tracking ---

  /**
   * Get paginated error feed (4xx/5xx requests)
   */
  getErrorFeed: async (firmId = null, page = 1, pageSize = 50, hours = 24) => {
    const params = { page, page_size: pageSize, hours };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/errors/feed', { params });
    return response.data;
  },

  /**
   * Get hourly error rates with spike detection
   */
  getErrorRates: async (firmId = null, days = 7) => {
    const params = { days };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/errors/rates', { params });
    return response.data;
  },

  /**
   * Get grouped errors by method+path+status_code
   */
  getErrorGroups: async (firmId = null, days = 7) => {
    const params = { days };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/errors/grouped', { params });
    return response.data;
  },

  /**
   * Get error detail with stack trace (from application_errors)
   */
  getErrorDetail: async (errorId) => {
    const response = await api.get(`/admin/observability/errors/detail/${errorId}`);
    return response.data;
  },

  // --- Integration Health ---

  /**
   * Get calendar sync health per user
   */
  getCalendarHealth: async (firmId = null, page = 1, pageSize = 50) => {
    const params = { page, page_size: pageSize };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/integrations/calendar', { params });
    return response.data;
  },

  /**
   * Get LawPay integration status per firm
   */
  getLawpayStatus: async (firmId = null, days = 30) => {
    const params = { days };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/integrations/lawpay', { params });
    return response.data;
  },

  /**
   * Get email/SMS delivery rates by communication type
   */
  getCommunicationRates: async (firmId = null, days = 30) => {
    const params = { days };
    if (firmId) params.firm_id = firmId;
    const response = await api.get('/admin/observability/integrations/communications', { params });
    return response.data;
  },

  // --- System Health ---

  /**
   * Get API response times ranked by p95
   */
  getResponseTimes: async (hours = 24) => {
    const params = { hours };
    const response = await api.get('/admin/observability/system/response-times', { params });
    return response.data;
  },

  /**
   * Get hourly response time trend (avg + p95)
   */
  getResponseTimeTrend: async (hours = 24) => {
    const params = { hours };
    const response = await api.get('/admin/observability/system/response-time-trend', { params });
    return response.data;
  },

  /**
   * Get database stats (tables, connections, pool, slow queries)
   */
  getDatabaseStats: async () => {
    const response = await api.get('/admin/observability/system/database-stats');
    return response.data;
  },

  /**
   * Get Docker container status or system info fallback
   */
  getContainerStatus: async () => {
    const response = await api.get('/admin/observability/system/container-status');
    return response.data;
  },
};

export default observabilityService;
